/**
 * Everything the ledger card decides, kept out of JSX so it can be tested.
 *
 * `vitest.config.ts` defines two node-only projects and collects only
 * `*.test.ts`; there is no DOM environment in this repository. Logic that
 * lives inside a component here is logic no test can reach, which is why this
 * module exists and why the components below it are close to markup.
 */

import type { Engine } from './cost-model';

export interface LedgerEntry {
  id: string;
  kind: string;
  amountMicroUsd: number;
  /** Null is meaningful: the entry has no duration comparable to the others. */
  billableSeconds: number | null;
  at: number;
  /** Present only when `/api/wallet/receipt/:id` can resolve this entry — see
   *  the server's `hasResolvableReceipt`. Absent, never false. */
  receiptAvailable?: true;
}

export interface LedgerGroup {
  groupId: string;
  kind: string;
  occurredAt: number;
  entries: LedgerEntry[];
  /** Present only when the session was billed text-only. Absent, never false —
   *  see the server's `LedgerGroup.textOnly` for why absence is not a claim. */
  textOnly?: true;
  /** The engine that served the group ("soniox" | "qwen"), when the server knows it. */
  engine?: string;
  /** The Qwen region that served the group ("sg" | "cn"), when the server knows it: a Qwen group
   *  whose charges name one. Absent for any other engine, and when the charges disagree. */
  region?: string;
  /** When the session began. Absent when the server cannot say. */
  startedAt?: number;
  /** When the session ended: its last charge, not a later correction in its group (`occurredAt`). */
  endedAt?: number;
}

const KIND_KEYS: Record<string, string> = {
  stt_speaker: 'sttSpeaker',
  stt_participant: 'sttParticipant',
  stt_mixed: 'sttMixed',
  stt: 'stt',
  translate_speaker: 'translateSpeaker',
  translate_participant: 'translateParticipant',
  tts: 'tts',
  tts_preview: 'ttsPreview',
  topup: 'topup',
  grant: 'grant',
  adjustment: 'adjustment',
  refund: 'refund',
  expired: 'expired',
  usage: 'usage',
  // GroupKind only -- the server's deriveGroupKind labels a group made
  // entirely of tts_preview entries this way (see wallet-ledger.ts). No
  // EntryKind is ever "voice_preview"; it reaches this table only through
  // groupLabelKey's fallback to entryLabelKey.
  voice_preview: 'voicePreview',
};

/**
 * The i18n key for an entry's label.
 *
 * An unknown kind falls back to the generic usage label rather than rendering
 * the raw enum: a deployed dashboard outlives the deploy that teaches the API
 * a new kind, and `stt_whisper` on a billing line is worse than "Usage".
 */
export function entryLabelKey(kind: string): string {
  const suffix = KIND_KEYS[kind] ?? 'usage';
  return `dashboard.wallet.ledger.kind.${suffix}`;
}

/** The i18n key for a group's label. Only a session needs a word of its own;
 *  every other group is a single entry and reuses that entry's label. */
export function groupLabelKey(kind: string): string {
  if (kind === 'session') return 'dashboard.wallet.ledger.group.session';
  return entryLabelKey(kind);
}

/**
 * A duration, or an empty string when there is none to state.
 *
 * Units are not localised. That used to be justified by matching
 * `formatRemaining` — the balance card's "about 6h 40m" — so the two would read
 * in one voice; that helper has since been removed along with the rest of the
 * balance card's hourly figures. The choice stands on its own: a ledger row's
 * "5m12s" is a compact technical duration sitting beside an amount, and
 * half-localising durations across a page is worse than localising none.
 *
 * A `seconds <= 0` charge renders BLANK, never `0s` — do not "fix" that. The
 * backend's `usageDescription()` (`billing-service.ts`) deliberately renders
 * the same case as `<1s`, because "no billable duration at all" (every
 * non-STT Soniox log, always `billable_seconds: 0`) and "a real duration
 * under half a second" (rounded to 0 by `Math.round`) are different facts
 * that must not read alike. But that distinction lives only in
 * `description`, which the privacy boundary (`wallet-ledger.ts`) withholds
 * from this response, and `billable_seconds` is 0 for both causes — the API
 * genuinely cannot carry which one happened, so this function can't either.
 * Rendering `0s` here would state a duration for a charge that may have none.
 */
export function formatDuration(seconds: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return '';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (h > 0) return `${h}h${pad(m)}m${pad(s)}s`;
  if (m > 0) return `${m}m${pad(s)}s`;
  return `${s}s`;
}

/**
 * A timestamp in the viewer's language.
 *
 * `Intl` takes BCP-47 tags directly, so the UI locale is passed straight
 * through. The code this replaces routed it through a four-entry map
 * (en/zh/ja/ko) falling back to 'en-US', which meant the eight other languages
 * this app ships all rendered US-formatted dates.
 *
 * It lives here rather than in a component because that is the only place a
 * test can reach it.
 */
export function formatLedgerDate(at: number, locale: string): string {
  return new Date(at).toLocaleString(locale, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/**
 * A session's start and end in the viewer's language: the start as
 * `formatLedgerDate` dates every row, then the end's time alone within one
 * day, or the end dated too across midnight (without the year when it is the
 * start's: the date column is shared by every row, so the longest range sets
 * its width). One time when both fall in the same minute.
 *
 * Built from `formatLedgerDate` rather than `Intl.DateTimeFormat.formatRange`,
 * which restyles the date part ("2026/10/20" where every other row reads
 * "2026年10月20日") and so would set session rows apart from the rest.
 */
export function formatLedgerRange(startedAt: number, endedAt: number, locale: string): string {
  const { start, separator, end } = ledgerRangeParts(startedAt, endedAt, locale);
  return end === null ? start : `${start}${separator}${end}`;
}

/**
 * `formatLedgerRange` in its three parts, so a row can keep each end whole
 * and wrap only at the separator on a narrow screen ("…02:05 PM–" / "02:10 PM",
 * never "…02:10" / "PM"). `end` is null when the range is one time.
 */
export function ledgerRangeParts(startedAt: number, endedAt: number, locale: string): {
  start: string; separator: string; end: string | null;
} {
  const start = formatLedgerDate(startedAt, locale);
  const timeOf = (at: number) => new Date(at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  if (new Date(startedAt).toDateString() !== new Date(endedAt).toDateString()) {
    const end = new Date(startedAt).getFullYear() === new Date(endedAt).getFullYear()
      ? new Date(endedAt).toLocaleString(locale, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : formatLedgerDate(endedAt, locale);
    return { start, separator: ' – ', end };
  }
  const end = timeOf(endedAt);
  return { start, separator: '–', end: end === timeOf(startedAt) ? null : end };
}

/** An engine's brand alone, for the calculator's engine switch: no region, as `engineName` adds one for Qwen. */
export function engineBrand(engine: Engine): string {
  return engine === 'qwen' ? 'Qwen' : 'Soniox';
}

/**
 * An engine's brand, as the app credits it ("Powered by Soniox"), or null for one it does not know.
 * Qwen also names the region its account is in: Alibaba bills Singapore and Beijing at different
 * prices. Only `sg` and `cn` name one; the server already reports `sg` for rows charged before
 * regions, so a Qwen group with any other region (none, because its charges disagree, or one this
 * page does not know) reads as the brand alone rather than as either region. Soniox has no region.
 */
export function engineName(
  engine: string | undefined,
  region: string | undefined,
  t: (key: string) => string,
): string | null {
  if (engine === 'soniox') return 'Soniox';
  if (engine === 'qwen') {
    if (region === 'sg') return t('dashboard.wallet.engine.qwenSingapore');
    if (region === 'cn') return t('dashboard.wallet.engine.qwenBeijing');
    return engineBrand('qwen');
  }
  return null;
}

/** The group's amount. Integer micro-USD throughout: summing the entries the
 *  client actually received is what makes an expanded group add up to its
 *  collapsed total, rather than trusting a separate aggregate. */
export function sumEntries(entries: LedgerEntry[]): number {
  return entries.reduce((total, e) => total + e.amountMicroUsd, 0);
}

/** One row of the expanded list, which is not one ledger entry — see
 *  `collapseEntries`. `count` is how many entries the row stands for. */
export interface DisplayEntry {
  key: string;
  kind: string;
  count: number;
  amountMicroUsd: number;
  billableSeconds: number | null;
}

/**
 * Kinds that are a METER reporting itself, as against a discrete event.
 *
 * The distinction is what `collapseEntries` turns on, so it is worth stating:
 * a meter reading is one stream telling us how much of itself it used, and N
 * of them in a row carry no more meaning than their sum. An event — a top-up,
 * a refund, a support adjustment — is a decision somebody made, with its own
 * reason and its own amount, and merging two of those destroys exactly what a
 * user came to this page to check.
 */
const METERED_KINDS = new Set([
  'stt_speaker', 'stt_participant', 'stt_mixed', 'stt', 'translate_speaker', 'translate_participant', 'tts', 'usage',
]);

/**
 * The expanded list, with repeated meter readings of one kind folded together.
 *
 * WHY THIS EXISTS. Soniox drops an idle TTS stream after ~5.3 seconds
 * (`src/routes/soniox.ts`, measured live against `tts-rt-v1`), so the client is
 * designed to reconnect after almost every conversational pause — and each
 * reconnect bills its own row. A forty-minute call therefore lands fifty
 * "Speech synthesis" lines in one group: fifty identical labels, no durations,
 * each a fraction of a cent. Expanding it answered nothing.
 *
 * One line reading "Speech synthesis ×50  -$0.92" answers the question the
 * user actually has, which is what synthesis cost them across the session.
 *
 * Amounts stay integer micro-USD and every entry is accounted for exactly
 * once, so the collapsed rows still add up to the group total shown above
 * them — that identity is the page's whole basis and folding must not cost it.
 */
export function collapseEntries(entries: LedgerEntry[]): DisplayEntry[] {
  const rows: DisplayEntry[] = [];
  const meteredByKind = new Map<string, DisplayEntry>();

  for (const entry of entries) {
    if (!METERED_KINDS.has(entry.kind)) {
      rows.push({
        key: entry.id,
        kind: entry.kind,
        count: 1,
        amountMicroUsd: entry.amountMicroUsd,
        billableSeconds: entry.billableSeconds,
      });
      continue;
    }

    const open = meteredByKind.get(entry.kind);
    if (!open) {
      // Keyed by kind, not by the first entry's id: at most one metered row per
      // kind survives, so the kind is unique within the group and stays stable
      // across re-renders in a way an id would not if paging reordered anything.
      const row: DisplayEntry = {
        key: entry.kind,
        kind: entry.kind,
        count: 1,
        amountMicroUsd: entry.amountMicroUsd,
        billableSeconds: entry.billableSeconds,
      };
      meteredByKind.set(entry.kind, row);
      rows.push(row);
      continue;
    }

    open.count += 1;
    open.amountMicroUsd += entry.amountMicroUsd;
    // Durations ADD here, where `groupDurationSeconds` takes a max — consistent,
    // not contradictory. Within one kind these rows are a single socket
    // reconnecting, so they follow each other in time. Across kinds the speaker
    // and the other party are talking at once. A kind that reported no duration
    // at all keeps `null`: summing nothing must not manufacture a `0s` claim.
    if (typeof entry.billableSeconds === 'number') {
      open.billableSeconds = (open.billableSeconds ?? 0) + entry.billableSeconds;
    }
  }

  return rows;
}

/**
 * How long the session lasted: the LONGEST stream, never the sum.
 *
 * The speaker and participant streams run concurrently for one call, so adding
 * them would tell a user a five-minute conversation took ten.
 *
 * Takes anything carrying a `billableSeconds` so it can run over the COLLAPSED
 * rows rather than the raw entries. That matters once `collapseEntries` has
 * summed a reconnecting stream's segments: the max belongs over each stream's
 * total, not over whichever single segment happened to be longest.
 */
export function groupDurationSeconds(entries: Array<{ billableSeconds: number | null }>): number | null {
  const durations = entries
    .map((e) => e.billableSeconds)
    .filter((s): s is number => typeof s === 'number' && Number.isFinite(s) && s > 0);
  return durations.length > 0 ? Math.max(...durations) : null;
}

/** Whether a group is worth a disclosure control. A single entry expanding to
 *  reveal only itself is noise. */
export function isExpandable(group: LedgerGroup): boolean {
  return group.entries.length > 1;
}

/** Where a "View receipt" link points. `encodeURIComponent` because a path
 *  segment assembled by concatenation is a traversal waiting for the first id
 *  that is not plain hex. */
export function receiptHref(entryId: string): string {
  return `/api/wallet/receipt/${encodeURIComponent(entryId)}`;
}

/** The entry id whose receipt this group can offer, or null. A group holds at
 *  most one receipt-bearing entry today — one mint per payment intent — so the
 *  first match is the answer. */
export function groupReceiptEntryId(group: LedgerGroup): string | null {
  return group.entries.find((e) => e.receiptAvailable)?.id ?? null;
}

/** Which translation mode a session ran in, in the app's own vocabulary. */
export type SessionMode = 'me' | 'other' | 'both';

/** A Qwen leg (`translate_*`) is a side of the call exactly as a Soniox stream is. */
const ME_KINDS = new Set(['stt_speaker', 'translate_speaker']);
const OTHER_KINDS = new Set(['stt_participant', 'translate_participant']);
/** One stream carrying mic and system audio together — shared Both. */
const MIXED_KINDS = new Set(['stt_mixed']);

/**
 * The mode a session ran in, or null when its entries do not say.
 *
 * The names are the APP'S: Me / Other / Both (`modePicker.modeYou` /
 * `modeParticipants` / `modeBoth`). Nothing new is invented here, and nothing
 * new is stored either — which streams were billed already implies which mode
 * the user picked.
 *
 * `stt_mixed` is Both as well. Shared Both bills ONE stream carrying both
 * sides, but the user still chose Both; "Combined" describes the stream and
 * belongs on the expanded entry, not on the row.
 *
 * Returns null for a plain `stt` entry (a provider that does not distinguish
 * sides) so the caller can fall back to the generic session label rather than
 * guess.
 */
export function sessionMode(group: LedgerGroup): SessionMode | null {
  // A generic "usage" charge cannot say whose side it was — a Qwen row with no
  // report on its lease, say — so the other sides' kinds no longer tell the mode.
  if (group.entries.some((e) => e.kind === 'usage')) return null;
  let me = false;
  let other = false;
  for (const entry of group.entries) {
    if (MIXED_KINDS.has(entry.kind)) return 'both';
    if (ME_KINDS.has(entry.kind)) me = true;
    if (OTHER_KINDS.has(entry.kind)) other = true;
  }
  if (me && other) return 'both';
  if (me) return 'me';
  if (other) return 'other';
  return null;
}

/**
 * Whether the session was billed as text-only.
 *
 * READ, NOT INFERRED. This used to answer "were there no synthesis charges?",
 * which is a different question: `config/soniox.ts` documents that the initial
 * TTS connect is best-effort and a speech session can legitimately bill no
 * synthesis at all, so the inference labelled those sessions "Text Only" while
 * the user had asked for spoken output.
 *
 * The server now derives it from the billing SKU and sends it only when it
 * KNOWS (see `LedgerGroup.textOnly`: absent, never false). Absent therefore
 * means speech-capable or unrecorded, and the row makes no claim either way.
 */
export function isTextOnlySession(group: LedgerGroup): boolean {
  return group.textOnly === true;
}

/** One stream's share of a session, for the row's duration bar. */
export interface StreamShare {
  kind: 'me' | 'other' | 'mixed';
  seconds: number;
}

/**
 * The billed streams of a session, longest first, with zero-length ones dropped.
 *
 * Runs over the COLLAPSED rows, so a stream that reconnected during the session
 * contributes its total rather than its last segment.
 */
export function streamShares(group: LedgerGroup): StreamShare[] {
  // As in `sessionMode`: with one charge's side unknown, the bar would show the rest alone.
  if (group.entries.some((e) => e.kind === 'usage')) return [];
  const shares: StreamShare[] = [];
  for (const row of collapseEntries(group.entries)) {
    const seconds = row.billableSeconds;
    if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) continue;
    if (ME_KINDS.has(row.kind)) shares.push({ kind: 'me', seconds });
    else if (OTHER_KINDS.has(row.kind)) shares.push({ kind: 'other', seconds });
    else if (MIXED_KINDS.has(row.kind)) shares.push({ kind: 'mixed', seconds });
  }
  return shares.sort((a, b) => b.seconds - a.seconds);
}

/**
 * How wide a session's duration bar is, as a percentage of the widest one.
 *
 * SQUARE ROOT, NOT LINEAR, and the reason is visible the moment a long session
 * shares a page with short ones: against a 90-minute call, a 5-minute one is
 * 5.8% wide and a 2-minute one 2.9% — two slivers a reader cannot tell apart,
 * on rows whose whole point is comparison. Taking the root keeps the order
 * exact (24% and 17% for the same pair) at the cost of the LENGTH RATIO, which
 * can no longer be read as "three times as long". The precise number sits in
 * the next column and remains the answer to that question; the bar is for
 * comparing at a glance.
 *
 * Scaled against the longest session currently loaded, so loading another page
 * can rescale every bar. That is correct rather than unfortunate: the bars
 * compare the rows a reader can actually see side by side.
 */
export function barWidthPercent(seconds: number, longestSeconds: number): number {
  if (!(seconds > 0) || !(longestSeconds > 0)) return 0;
  const ratio = Math.min(1, seconds / longestSeconds);
  return Math.sqrt(ratio) * 100;
}

/** The longest session among the rows on screen — the bar scale's denominator. */
export function longestGroupSeconds(groups: LedgerGroup[]): number {
  let longest = 0;
  for (const group of groups) {
    const seconds = groupDurationSeconds(collapseEntries(group.entries));
    if (typeof seconds === 'number' && seconds > longest) longest = seconds;
  }
  return longest;
}
